"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BoxPlotChart = exports.buildBoxPlotTooltip = exports.buildBoxPlotTooltipRows = void 0;
/*
 * © 2026 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
const react_1 = __importStar(require("react"));
const react_dom_1 = require("react-dom");
const react_i18next_1 = require("react-i18next");
const boxPlot_1 = require("@centralwebteam/narwhal/lib/math/boxPlot");
const colours_1 = require("../colours");
const styled_components_1 = require("../styled-components");
const EmptyState_1 = require("../EmptyState");
const formatting_1 = require("../Text/formatting");
const ToolTip_1 = __importDefault(require("../ToolTip"));
const ToleranceScale_1 = require("../ToleranceScale");
const Text_1 = require("../Text");
/** Fraction of the tolerance range shown either side of the spec limits by default, giving the 3:20:3 ratio */
const OUTER_BAND_FRACTION = 3 / 20;
const GRADIENT_ID = "gradient-oneSided";
/** Opacity of the deviation graph background tolerance zones, matching the measurement tolerance graphic */
const ZONE_OPACITY = 0.4;
/** Box plot element dimensions, as defined in the box plot graph breakdown */
const END_CAP_WIDTH = 1;
const END_CAP_HEIGHT = 6;
const WHISKER_HEIGHT = 2;
const BOX_HEIGHT = 10;
const BOX_STROKE_WIDTH = 1;
const MEDIAN_WIDTH = 2;
const BOX_FILL = "#6B6B6B";
const PLOT_STROKE = "#FFFFFF";
/** Colour of the specification limit lines, matching the measurement tolerance graphic */
const LIMIT_LINE_COLOUR = "#b0b0b0";
const buildBoxPlotTooltipRows = ({ nominal, lowerLimit, upperLimit, actuals = [], stats, t, }) => {
    const translate = t !== null && t !== void 0 ? t : ((key, options = {}) => {
        var _a;
        const count = Number((_a = options.count) !== null && _a !== void 0 ? _a : 0);
        switch (key) {
            case "label-boxplot-at-nominal":
                return "At Nominal";
            case "label-boxplot-below-lsl":
                return "below LSL";
            case "label-boxplot-above-usl":
                return "above USL";
            case "label-boxplot-from-nominal":
                return "from nominal";
            case "label-boxplot-result-count_one":
                return `${count} result`;
            case "label-boxplot-result-count_other":
                return `${count} results`;
            default:
                return key;
        }
    });
    const boxPlotStats = stats !== null && stats !== void 0 ? stats : (0, boxPlot_1.getBoxPlotStatistics)(actuals);
    if (!boxPlotStats) {
        return [];
    }
    const { min, q1, median, q3, max } = boxPlotStats;
    const formatStatistic = (value) => (0, formatting_1.tidyNumber)(value);
    const resolvedNominal = nominal !== null && nominal !== void 0 ? nominal : (lowerLimit !== undefined && upperLimit !== undefined
        ? (lowerLimit + upperLimit) / 2
        : undefined);
    const medianDelta = resolvedNominal !== undefined ? median - resolvedNominal : undefined;
    const isMedianAtNominal = medianDelta !== undefined && Math.abs(medianDelta) < Number.EPSILON;
    const buildMedianDescription = () => {
        if (medianDelta === undefined) {
            return "";
        }
        if (isMedianAtNominal) {
            return translate("label-boxplot-at-nominal");
        }
        return translate("label-boxplot-from-nominal");
    };
    const rows = [];
    const belowLowerLimit = lowerLimit !== undefined
        ? actuals.filter((value) => value < lowerLimit).length
        : undefined;
    rows.push({
        shortLabel: "Min",
        label: "Minimum",
        value: formatStatistic(min),
        highlight: belowLowerLimit !== undefined
            ? translate(`label-boxplot-result-count_${belowLowerLimit === 1 ? "one" : "other"}`, { count: belowLowerLimit })
            : undefined,
        description: lowerLimit !== undefined ? translate("label-boxplot-below-lsl") : "",
    });
    rows.push({
        shortLabel: "Q1",
        label: "Lower quartile (Q1)",
        value: formatStatistic(q1),
        description: "",
    }, {
        shortLabel: "Median",
        label: "Median",
        value: formatStatistic(median),
        highlight: medianDelta !== undefined && !isMedianAtNominal
            ? `${medianDelta > 0 ? "+" : ""}${formatStatistic(medianDelta)}`
            : undefined,
        description: buildMedianDescription(),
    }, {
        shortLabel: "Q3",
        label: "Upper quartile (Q3)",
        value: formatStatistic(q3),
        description: "",
    });
    const aboveUpperLimit = upperLimit !== undefined
        ? actuals.filter((value) => value > upperLimit).length
        : undefined;
    rows.push({
        shortLabel: "Max",
        label: "Maximum",
        value: formatStatistic(max),
        highlight: aboveUpperLimit !== undefined
            ? translate(`label-boxplot-result-count_${aboveUpperLimit === 1 ? "one" : "other"}`, { count: aboveUpperLimit })
            : undefined,
        description: upperLimit !== undefined ? translate("label-boxplot-above-usl") : "",
    });
    return rows;
};
exports.buildBoxPlotTooltipRows = buildBoxPlotTooltipRows;
const buildBoxPlotTooltip = ({ nominal, lowerLimit, upperLimit, actuals = [], stats, t, }) => (0, exports.buildBoxPlotTooltipRows)({
    nominal,
    lowerLimit,
    upperLimit,
    actuals,
    stats,
    t,
})
    .map((row) => {
    var _a;
    return `${row.shortLabel} ${row.value} ${(_a = row.highlight) !== null && _a !== void 0 ? _a : ""} ${row.description}`.replace(/\s+/g, " ");
})
    .join("\n");
exports.buildBoxPlotTooltip = buildBoxPlotTooltip;
/** A box plot of a set of measurement results, shown against the same 3:20:3 background as the deviation graph */
const BoxPlotChart = ({ nominal, lowerLimit, upperLimit, actuals, stats, width = 400, height = 20, }) => {
    var _a;
    const { t } = (0, react_i18next_1.useTranslation)("central-web-app-components");
    const [hoverPosition, setHoverPosition] = (0, react_1.useState)(undefined);
    const [tooltipWidth, setTooltipWidth] = (0, react_1.useState)(0);
    const [tooltipHeight, setTooltipHeight] = (0, react_1.useState)(0);
    const tooltipRef = (0, react_1.useRef)(null);
    (0, react_1.useLayoutEffect)(() => {
        if (hoverPosition && tooltipRef.current) {
            setTooltipWidth(tooltipRef.current.offsetWidth);
            setTooltipHeight(tooltipRef.current.offsetHeight);
        }
    }, [hoverPosition]);
    const boxPlotStats = stats !== null && stats !== void 0 ? stats : (0, boxPlot_1.getBoxPlotStatistics)(actuals !== null && actuals !== void 0 ? actuals : []);
    if (!boxPlotStats) {
        return react_1.default.createElement(EmptyState_1.EmptyState, { text: "message-NoBoxPlotData" });
    }
    const { min, q1, median, q3, max } = boxPlotStats;
    const toleranceDirection = (0, ToleranceScale_1.getAbsoluteToleranceDirection)(lowerLimit, upperLimit);
    const tooltipRows = (0, exports.buildBoxPlotTooltipRows)({
        nominal,
        lowerLimit,
        upperLimit,
        actuals: actuals !== null && actuals !== void 0 ? actuals : [],
        stats: boxPlotStats,
        t,
    });
    const tooltipContent = (react_1.default.createElement(TooltipTable, null,
        react_1.default.createElement(Text_1.Text, { style: { fontWeight: 600 } }, t("label-Nominal")),
        react_1.default.createElement(TooltipCell, null, nominal !== undefined ? ((0, formatting_1.tidyNumber)(nominal)) : lowerLimit !== undefined && upperLimit !== undefined ? ((0, formatting_1.tidyNumber)((lowerLimit + upperLimit) / 2)) : (react_1.default.createElement(Text_1.Text, null))),
        react_1.default.createElement(TooltipCell, null, lowerLimit !== undefined ? (t("label-boxplot-lsl", { value: (0, formatting_1.tidyNumber)(lowerLimit) })) : (react_1.default.createElement(Text_1.Text, null))),
        react_1.default.createElement(TooltipCell, null, upperLimit !== undefined ? (t("label-boxplot-usl", { value: (0, formatting_1.tidyNumber)(upperLimit) })) : (react_1.default.createElement(Text_1.Text, null))),
        tooltipRows.map((row) => {
            var _a;
            return (react_1.default.createElement(react_1.default.Fragment, { key: row.label },
                react_1.default.createElement(Text_1.Text, { style: { fontWeight: 600 } }, row.label),
                react_1.default.createElement(TooltipCell, null, row.value),
                react_1.default.createElement(TooltipCell, null, (_a = row.highlight) !== null && _a !== void 0 ? _a : ""),
                react_1.default.createElement(TooltipCell, null, row.description)));
        })));
    // Tolerance range is only known where both limits are defined; otherwise fall back to the data spread
    const toleranceRange = lowerLimit !== undefined && upperLimit !== undefined
        ? upperLimit - lowerLimit
        : (max - min || 1) / (1 - 2 * OUTER_BAND_FRACTION);
    const outerBand = toleranceRange * OUTER_BAND_FRACTION;
    const centre = lowerLimit !== undefined && upperLimit !== undefined
        ? (lowerLimit + upperLimit) / 2
        : ((_a = lowerLimit !== null && lowerLimit !== void 0 ? lowerLimit : upperLimit) !== null && _a !== void 0 ? _a : (min + max) / 2);
    const defaultMin = (lowerLimit !== null && lowerLimit !== void 0 ? lowerLimit : centre) - outerBand;
    const defaultMax = (upperLimit !== null && upperLimit !== void 0 ? upperLimit : centre) + outerBand;
    // The scale adjusts to keep the min and max visible, if they fall outside the default 3:20:3 range
    const domainMin = Math.min(defaultMin, min);
    const domainMax = Math.max(defaultMax, max);
    // Single-sided tolerances only have a "used" half of the graphic (the other half is the hatched
    // UnusedSideZone), so the domain must be confined to that half rather than spanning the full width.
    const xRange = toleranceDirection === "single positive"
        ? [width / 2, width]
        : toleranceDirection === "single negative"
            ? [0, width / 2]
            : [0, width];
    const x = (0, ToleranceScale_1.createLinearScale)([domainMin, domainMax], xRange);
    // The graphic has a shadow border top and bottom, so the height of the SVG is 2px less.
    const svgHeight = height - 2;
    const centreY = svgHeight / 2;
    const boxX = Math.min(x(q1), x(q3));
    const boxWidth = Math.abs(x(q3) - x(q1));
    return (react_1.default.createElement(Container, null,
        react_1.default.createElement(Wrapper, { width: width, onMouseMove: (event) => setHoverPosition({ x: event.clientX, y: event.clientY }), onMouseLeave: () => setHoverPosition(undefined) },
            react_1.default.createElement("svg", { style: { flex: "0 1 auto", maxWidth: "100%" }, width: width, height: svgHeight, viewBox: `0 0 ${width} ${svgHeight}` },
                react_1.default.createElement("defs", null,
                    react_1.default.createElement(ToleranceScale_1.OneSidedGradient, { id: GRADIENT_ID, direction: toleranceDirection })),
                react_1.default.createElement(ToleranceScale_1.UnusedSideZone, { direction: toleranceDirection, width: width, height: svgHeight, gradientId: GRADIENT_ID }),
                react_1.default.createElement("g", { opacity: ZONE_OPACITY },
                    lowerLimit !== undefined && (react_1.default.createElement(ToleranceScale_1.Zone, { x1: 0, x2: x(lowerLimit), height: svgHeight, fill: colours_1.colours.Fail })),
                    upperLimit !== undefined && (react_1.default.createElement(ToleranceScale_1.Zone, { x1: x(upperLimit), x2: width, height: svgHeight, fill: colours_1.colours.Fail }))),
                [lowerLimit, upperLimit].map((limit, index) => limit === undefined ? null : (react_1.default.createElement("path", { key: `limit${index}`, d: `M${Math.round(x(limit))},0V${svgHeight}`, stroke: LIMIT_LINE_COLOUR, shapeRendering: "crispEdges" }))),
                react_1.default.createElement("rect", { x: x(min), y: centreY - WHISKER_HEIGHT / 2, width: Math.abs(x(max) - x(min)), height: WHISKER_HEIGHT, fill: PLOT_STROKE, shapeRendering: "crispEdges" }),
                [min, max].map((value, index) => {
                    // Clamp so the cap stays fully on-canvas even when min/max sits exactly at the domain edge
                    const capX = Math.min(Math.max(x(value), END_CAP_WIDTH / 2), width - END_CAP_WIDTH / 2);
                    return (react_1.default.createElement("rect", { key: `cap${index}`, x: capX - END_CAP_WIDTH / 2, y: centreY - END_CAP_HEIGHT / 2, width: END_CAP_WIDTH, height: END_CAP_HEIGHT, fill: PLOT_STROKE, shapeRendering: "crispEdges" }));
                }),
                react_1.default.createElement("rect", { x: boxX + BOX_STROKE_WIDTH / 2, y: centreY - BOX_HEIGHT / 2 + BOX_STROKE_WIDTH / 2, width: Math.max(boxWidth - BOX_STROKE_WIDTH, 0), height: BOX_HEIGHT - BOX_STROKE_WIDTH, fill: BOX_FILL, stroke: PLOT_STROKE, strokeWidth: BOX_STROKE_WIDTH, shapeRendering: "crispEdges" }),
                react_1.default.createElement("rect", { x: x(median) - MEDIAN_WIDTH / 2, y: centreY - BOX_HEIGHT / 2, width: MEDIAN_WIDTH, height: BOX_HEIGHT, fill: PLOT_STROKE, shapeRendering: "crispEdges" }))),
        (0, react_dom_1.createPortal)(react_1.default.createElement(ToolTip_1.default, { ref: tooltipRef, show: hoverPosition !== undefined, position: hoverPosition && {
                x: tooltipWidth > 0 &&
                    hoverPosition.x + 10 + tooltipWidth > window.innerWidth
                    ? hoverPosition.x - 10 - tooltipWidth
                    : hoverPosition.x + 10,
                y: tooltipHeight > 0 &&
                    hoverPosition.y + 10 + tooltipHeight > window.innerHeight
                    ? hoverPosition.y - 10 - tooltipHeight
                    : hoverPosition.y + 10,
            }, content: tooltipContent, style: {
                position: "fixed",
                whiteSpace: "normal",
                pointerEvents: "none",
                zIndex: 9999,
            } }), document.body)));
};
exports.BoxPlotChart = BoxPlotChart;
const TooltipCell = (0, styled_components_1.styled)(Text_1.Text) `
  text-align: left;
  opacity: 0.8;
  border-left: 1px solid #fff4;
  padding-left: 10px;
  align-self: stretch;
`;
const TooltipTable = styled_components_1.styled.div `
  display: grid;
  grid-template-columns: repeat(4, max-content);
  align-items: center;
  width: max-content;
  gap: 5px 10px;
  font-size: 12px;
  line-height: 1.4;
  white-space: nowrap;
`;
const Container = styled_components_1.styled.div `
  position: relative;
`;
const Wrapper = styled_components_1.styled.figure `
  border-radius: ${({ theme }) => theme.radius};
  overflow: hidden;
  display: flex;
  width: ${({ width }) => width + "px"};
  margin: 0;
  padding: 1px 0;
  background: ${({ theme }) => theme.bg};
  position: relative;
  // Overlay the box-shadows on top of the graphic as a pseudo-element
  &::after {
    content: " ";
    border-radius: ${({ theme }) => theme.radius};
    box-shadow: ${({ theme }) => theme.boxShadows.inset};
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    pointer-events: none;
  }
`;
//# sourceMappingURL=BoxPlotChart.js.map